import { useMemo } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import AutocompleteFormField from "components/autocomplete-form-field";
import TeamPill from "components/team-pill";
import { useGetTeamsQuery } from "services/teams";

/** The form value: the team's id as a string (like the assignee picker), its name and colour. */
export type TeamOption = { label: string; value: string; color: string };

type TeamSelectFormFieldProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  /** The person picked beside it, so the helper can say what a team does with the task. */
  disabled?: boolean;
  onOptionSelected?: (team: TeamOption | null) => void;
};

/**
 * Teams (TEAMS_FEATURE): "Send to a team" on the task forms. Picking a team sends the task to every
 * member's Home → For me as Unclaimed; the first to click Take it owns it. Options are the
 * practice's active teams. Rendered only with the flag on, so an older backend never sees `team`.
 */
export default function TeamSelectFormField<T extends FieldValues>({
  name,
  control,
  label = "Send to a team",
  disabled,
  onOptionSelected
}: TeamSelectFormFieldProps<T>) {
  const { data, isLoading } = useGetTeamsQuery({ is_active: true });
  const options: TeamOption[] = useMemo(
    () =>
      (data?.results ?? []).map((team) => ({
        label: team.name,
        value: String(team.id),
        color: team.color
      })),
    [data]
  );

  return (
    <Box>
      <AutocompleteFormField<TeamOption, T>
        name={name}
        control={control}
        label={label}
        options={options}
        isLoading={isLoading}
        disabled={disabled}
        getOptionLabel={(option) => option?.label ?? ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        noOptionsText="No teams yet. An administrator creates them in Administration → Teams."
        onOptionSelected={(option) =>
          onOptionSelected?.((option as TeamOption | null) ?? null)
        }
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <TeamPill name={option.label} color={option.color} />
          </li>
        )}
        sx={{ ml: 0 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        A person or a team. A team task shows in every member&apos;s task drawer
        and on their Home screen; the first to click <b>Take it</b> owns it.
      </Typography>
    </Box>
  );
}
